import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ShoppingCart, Plus, Minus, Leaf, Drumstick, Store, Package,
  MapPin, LogIn, Search, X, RotateCcw, TrendingUp, Flame, Clock,
  ChevronDown, ChevronUp, Receipt, Check, SlidersHorizontal, Tag,
  Sparkles, Timer, Circle, CheckCircle2
} from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { GradientPlaceholder } from './partner/GradientPlaceholder';
import axios from 'axios';
import { host, GOOGLE_CLIENT_ID, TAXES,TAX_TYPE_ENUM,resolveTaxType,computeTaxBreakdown } from './script/variables';
import './style/menuitem.css';

import SEOMeta from './common/SEOMeta';
import { Link } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────
   DUMMY "ordered today" counts — replace with real API data.
   Key = dishId, value = count (seeded so stable per session).
───────────────────────────────────────────────────────────── */
const _dummyCache = {};
const getDummyCount = (dishId) => {
  if (!_dummyCache[dishId]) {
    const seed = String(dishId).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    _dummyCache[dishId] = (seed % 47) + 3; // 3–49
  }
  return _dummyCache[dishId];
};

const MOST_ORDERED_TOP = 5;
const TABLE_POLL_INTERVAL_MS = 3000;
const TOAST_DURATION_MS = 2300;
const COMBO_COUNTDOWN_WINDOW_MS = 48 * 60 * 60 * 1000; // only show a timer if <48h remain

/* ─────────────────────────────────────────────────────────────
   Price / discount helpers — used by sorting & filtering.
   A dish can have multiple sizes (small/medium/large); we pick a
   single "representative" price (medium first, else whichever
   size is available) so sort/filter behaves predictably.
───────────────────────────────────────────────────────────── */
const SIZE_PRIORITY = ['medium', 'small', 'large'];

const getRepresentativeSize = (dish) =>
  SIZE_PRIORITY.find(sz => dish[sz]?.price > 0) || null;

const getDisplayPrice = (dish) => {
  const sz = getRepresentativeSize(dish);
  if (!sz) return 0;
  const { price, discount = 0 } = dish[sz];
  return Math.round((price - (price * discount) / 100) * 100) / 100;
};

const dishHasDiscount = (dish) =>
  ['small', 'medium', 'large'].some(sz => (dish[sz]?.discount || 0) > 0);

const PRICE_RANGES = [
  { key: 'all',       label: 'Any Price' },
  { key: 'under200',  label: 'Under ₹200' },
  { key: '200to400',  label: '₹200 – ₹400' },
  { key: 'above400',  label: 'Above ₹400' },
];

const SORT_OPTIONS = [
  { key: 'default',   label: 'Recommended' },
  { key: 'priceLow',  label: 'Price: Low to High' },
  { key: 'priceHigh', label: 'Price: High to Low' },
];

/* ─────────────────────────────────────────────────────────────
   Combo helpers
───────────────────────────────────────────────────────────── */

// Normalizes whatever shape resolveComboPricing() sends back on
// combo.pricing into { regularPrice, finalPrice, savings }. Checks a
// couple of likely alternate field names so this keeps working even
// if the exact key names on the backend differ slightly.
//
// finalPrice here is the OWNER-CONFIGURED combo price (fixed price, or
// regular-minus-discount as set on the combo itself) — this is always
// what the customer is actually charged and must never be recalculated
// client-side from anything the customer selects.
const getComboPricing = (combo) => {
  const isChoice     = combo.comboStructure === 'CHOICE';
  const isFixedPrice = !!combo.isFixedPrice;
  const fixedPrice   = Number(combo.fixedPrice) || 0;
  const discountType = combo.discountType;
  const discount     = Number(combo.discount) || 0;

  if (isChoice) {
    const { min: minOriginal, max: maxOriginal } = getChoiceOriginalPriceRange(combo);

    const minFinal = isFixedPrice ? fixedPrice : applyComboDiscount(minOriginal, discountType, discount);
    const maxFinal = isFixedPrice ? fixedPrice : applyComboDiscount(maxOriginal, discountType, discount);

    const minSavings = Math.max(0, Math.round((minOriginal - minFinal) * 100) / 100);
    const maxSavings = Math.max(0, Math.round((maxOriginal - maxFinal) * 100) / 100);

    return {
      isChoice: true,
      isFixedPrice,
      regularPrice: minOriginal,
      finalPrice: minFinal,
      savings: minSavings,
      maxSavings,
      minOriginal,
      maxOriginal,
    };
  }

  // BUNDLE
  const originalPrice = getBundleOriginalPrice(combo);
  const finalPrice = isFixedPrice ? fixedPrice : applyComboDiscount(originalPrice, discountType, discount);
  const savings = Math.max(0, Math.round((originalPrice - finalPrice) * 100) / 100);

  return {
    isChoice: false,
    isFixedPrice,
    regularPrice: originalPrice,
    finalPrice,
    savings,
    maxSavings: savings,
    minOriginal: originalPrice,
    maxOriginal: originalPrice,
  };
};

const COMBO_OPTION_SIZE_PRIORITY = ['medium', 'small', 'large'];

// Prices ONE dish at a given size from its populated Dish doc (raw
// dish.sizes.small.price shape — used by both a BUNDLE combo's fixed
// combo.dishes[] and a CHOICE combo's choiceGroups[].options[]). Falls
// back to a representative size if the given size has no price.
// Returns both price and the dish's own discount so every combo line
// keeps a full record (price + discount), not just a single number.
const getComboDishPriceInfo = (dish, size) => {
  if (!dish) return { price: 0, discount: 0 };
  const sizeData = size ? dish.sizes?.[size] : null;
  if (sizeData?.price) return { price: Number(sizeData.price) || 0, discount: Number(sizeData.discount) || 0 };
  for (const sz of COMBO_OPTION_SIZE_PRIORITY) {
    if (dish.sizes?.[sz]?.price) {
      return { price: Number(dish.sizes[sz].price) || 0, discount: Number(dish.sizes[sz].discount) || 0 };
    }
  }
  return { price: 0, discount: 0 };
};

// Applies a combo-level discount to a base amount. PERCENT takes a
// percentage off; anything else (FLAT / AMOUNT) is a flat rupee amount
// off, floored at 0 so a large flat discount can't go negative.
const applyComboDiscount = (amount, discountType, discount) => {
  const amt  = Number(amount) || 0;
  const disc = Number(discount) || 0;
  if (!disc) return Math.round(amt * 100) / 100;
  if (discountType === 'PERCENT') {
    return Math.max(0, Math.round((amt - (amt * disc) / 100) * 100) / 100);
  }
  return Math.max(0, Math.round((amt - disc) * 100) / 100);
};

// BUNDLE combos have fixed dishes — only one possible regular price.
const getBundleOriginalPrice = (combo) => {
  const total = (combo.dishes || []).reduce((sum, d) => {
    const info = getComboDishPriceInfo(d.dishId, d.size);
    return sum + info.price * (d.quantity || 1);
  }, 0);
  return Math.round(total * 100) / 100;
};

// One CHOICE group's cheapest/priciest possible totals, respecting
// minSelect/maxSelect. Excludes unavailable/removed dishes — those
// can't actually be picked.
const getChoiceGroupPriceRange = (group) => {
  const prices = (group.options || [])
    .filter(opt => opt.dishId && opt.dishId.isAvailable !== false && !opt.dishId.isRemoved)
    .map(opt => {
      const info = getComboDishPriceInfo(opt.dishId, opt.size);
      return info.price * (opt.quantity || 1);
    });
  if (!prices.length) return { min: 0, max: 0 };

  const minSelect = group.minSelect || 1;
  const maxSelect = group.maxSelect || 1;

  const ascending  = [...prices].sort((a, b) => a - b);
  const descending = [...prices].sort((a, b) => b - a);

  return {
    min: ascending.slice(0, minSelect).reduce((s, p) => s + p, 0),
    max: descending.slice(0, maxSelect).reduce((s, p) => s + p, 0),
  };
};

// Sums min/max ranges across every group — cheapest and priciest a
// CHOICE combo's regular price could ever be. Powers the card's
// "starting from" price and "save up to" figure.
const getChoiceOriginalPriceRange = (combo) => {
  const range = (combo.choiceGroups || []).reduce((acc, g) => {
    const r = getChoiceGroupPriceRange(g);
    return { min: acc.min + r.min, max: acc.max + r.max };
  }, { min: 0, max: 0 });
  return {
    min: Math.round(range.min * 100) / 100,
    max: Math.round(range.max * 100) / 100,
  };
};



const getInitials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

const formatCountdown = (ms) => {
  if (ms == null || ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const COMBO_GRADIENTS = [
  'linear-gradient(135deg,#E8A135,#C0472B)',
  'linear-gradient(135deg,#4A7FA6,#2C4A63)',
  'linear-gradient(135deg,#C9A876,#9B7D4F)',
  'linear-gradient(135deg,#B8925E,#8A6A3F)',
];

/* ─────────────────────────────────────────────────────────────
   VegNonVegIcon — the familiar green/red square-with-dot symbol,
   used everywhere in place of plain "veg"/"nonveg" text.
───────────────────────────────────────────────────────────── */
const VegNonVegIcon = ({ type, size = 13 }) => {
  if (type !== 'veg' && type !== 'nonveg') return null;
  return (
    <span
      className={`mi-vnv ${type === 'veg' ? 'mi-vnv-veg' : 'mi-vnv-nonveg'}`}
      style={{ width: size, height: size }}
      aria-label={type === 'veg' ? 'Vegetarian' : 'Non-Vegetarian'}
      title={type === 'veg' ? 'Vegetarian' : 'Non-Vegetarian'}
    >
      <span className="mi-vnv-dot" />
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════
   ComboPlaceholder — gradient tile with initials, used when a
   combo has no image (mirrors GradientPlaceholder for dishes).
═══════════════════════════════════════════════════════════ */
const ComboPlaceholder = ({ name, index = 0 }) => (
  <div
    className="mi-combo-placeholder"
    style={{ background: COMBO_GRADIENTS[index % COMBO_GRADIENTS.length] }}
  >
    <span>{getInitials(name)}</span>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   ComboCard — one card in the horizontal "Combos & Offers" row
═══════════════════════════════════════════════════════════ */
const ComboCard = ({ combo, index, onOpen, nowTick }) => {
  const pricing   = getComboPricing(combo);
  const hasImage  = combo.image && !['#', 'null', 'undefined', '.', ''].includes(String(combo.image).trim());
  const ctaLabel  = combo.comboStructure === 'CHOICE' ? 'Customize & Add' : 'View & Add';

  // CHOICE combo prices move with the guest's picks — show a starting
  // price plus the best-case ("up to") saving instead of one fixed number.
  const isVariablePricing = pricing.isChoice;
  const displaySavings = isVariablePricing ? pricing.maxSavings : pricing.savings;

  const countdownMs = combo.validTill ? new Date(combo.validTill).getTime() - nowTick : null;
  const showCountdown = countdownMs != null && countdownMs > 0 && countdownMs < COMBO_COUNTDOWN_WINDOW_MS;

  return (
    <div className="mi-combo-card">
      <div className="mi-combo-img-box">
        {hasImage
          ? <img src={combo.image} alt={combo.name} className="mi-combo-img" loading="lazy" />
          : <ComboPlaceholder name={combo.name} index={index} />
        }
        {combo.tag && <span className="mi-combo-tag-badge">{combo.tag}</span>}
        {showCountdown && (
          <span className="mi-combo-timer">
            <Timer size={10} />{formatCountdown(countdownMs)} left
          </span>
        )}
      </div>

      <div className="mi-combo-body">
        <h3 className="mi-combo-name">{combo.name}</h3>
        {combo.description && <p className="mi-combo-desc">{combo.description}</p>}

        <div className="mi-combo-price-row">
          <span className="mi-combo-price">
            {isVariablePricing ? `From ₹${pricing.finalPrice}` : `₹${pricing.finalPrice}`}
          </span>
          {pricing.regularPrice > pricing.finalPrice && (
            <span className="mi-combo-old-price">₹{pricing.regularPrice}</span>
          )}
          {displaySavings > 0 && (
            <span className="mi-combo-save-pill">
              {isVariablePricing ? `SAVE UP TO ₹${displaySavings}` : `SAVE ₹${displaySavings}`}
            </span>
          )}
        </div>

        <button className="mi-combo-cta" onClick={() => onOpen(combo)}>
          {ctaLabel}
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   ComboModal — handles both BUNDLE (read-only preview) and
   CHOICE (pick-N-per-group) combos in one shared UI.

  Pricing rules:
   - Two combo types exist, controlled by combo.isFixedPrice:
       • isFixedPrice: true  → finalPrice is ALWAYS combo.fixedPrice,
         the owner-configured price. Never changes with selection.
       • isFixedPrice: false → finalPrice = combo.discountType/
         combo.discount applied to the REGULAR price of what's
         actually selected. For CHOICE combos this means finalPrice
         moves live as the guest swaps options (15% off ₹240 worth of
         picks ≠ 15% off ₹300 worth of picks).
   - regularPrice ("what these dishes would cost separately") DOES move
     for CHOICE combos, recomputed live from the current selection.
   - savings = regularPrice − finalPrice.
═══════════════════════════════════════════════════════════ */
const ComboModal = ({ combo, onClose, onAdd }) => {
  const isChoice = combo.comboStructure === 'CHOICE';
  const groups   = combo.choiceGroups || [];

  // { [groupIndex]: Set(optionIndex) }
  const [selected, setSelected] = useState({});

  const toggleOption = (gi, oi, maxSelect) => {
    setSelected(prev => {
      const current = new Set(prev[gi] || []);
      if (maxSelect === 1) {
        // radio behaviour — single choice replaces whatever was picked
        return { ...prev, [gi]: new Set([oi]) };
      }
      if (current.has(oi)) current.delete(oi);
      else if (current.size < maxSelect) current.add(oi);
      return { ...prev, [gi]: current };
    });
  };

  // Full dish-level breakdown for THIS combo. BUNDLE: fixed, from
  // combo.dishes[]. CHOICE: whatever's currently selected. Each line
  // carries {dishId, name, size, quantity, price, discount}.
  const dishesBreakdown = useMemo(() => {
    if (!isChoice) {
      return (combo.dishes || []).map(d => {
        const info = getComboDishPriceInfo(d.dishId, d.size);
        return {
          dishId: d.dishId?._id || d.dishId,
          name: d.dishId?.name || '',
          size: d.size || null,
          quantity: d.quantity || 1,
          price: info.price,
          discount: info.discount,
        };
      });
    }
    const lines = [];
    groups.forEach((g, gi) => {
      (selected[gi] ? Array.from(selected[gi]) : []).forEach(oi => {
        const opt = g.options?.[oi];
        const dish = opt?.dishId;
        if (!dish) return;
        const info = getComboDishPriceInfo(dish, opt.size);
        lines.push({
          dishId: dish._id || dish,
          name: dish.name || '',
          size: opt.size || null,
          quantity: opt.quantity || 1,
          price: info.price,
          discount: info.discount,
        });
      });
    });
    return lines;
  }, [isChoice, combo, groups, selected]);

  // "What these dishes would cost separately" — derived directly from
  // dishesBreakdown so the number shown and the number stored always
  // come from the same source.
  const liveRegularPrice = useMemo(() => {
    if (!isChoice) return null;
    const total = dishesBreakdown.reduce((s, d) => s + d.price * d.quantity, 0);
    return Math.round(total * 100) / 100;
  }, [isChoice, dishesBreakdown]);

  // Owner-configured price — same for every selection, BUNDLE or
  // CHOICE. This is the one number that ends up on the Add button and
  // gets charged; it is deliberately never derived from `selected`.
 const isFixedPrice = !!combo.isFixedPrice;

  const pricing = isChoice
    ? (() => {
        const regularPrice = liveRegularPrice || 0;
        const finalPrice = isFixedPrice
          ? (Number(combo.fixedPrice) || 0)
          : applyComboDiscount(regularPrice, combo.discountType, combo.discount);
        const savings = Math.max(0, Math.round((regularPrice - finalPrice) * 100) / 100);
        return { regularPrice, finalPrice, savings };
      })()
    : getComboPricing(combo);

  const isValid = !isChoice || groups.every((g, gi) => {
    const count = selected[gi]?.size || 0;
    return count >= (g.minSelect || 1) && count <= (g.maxSelect || 1);
  });

  const handleAdd = () => {
    if (!isValid) return;
    const selections = [];
    if (isChoice) {
      groups.forEach((g, gi) => {
        (selected[gi] ? Array.from(selected[gi]) : []).forEach(oi => {
          const opt = g.options?.[oi];
          const dish = opt?.dishId;
          if (dish) {
            selections.push({
              groupName: g.groupName,
              dishId: dish._id || dish,
              dishName: dish.name || '',
              size: opt.size,
            });
          }
        });
      });
    }
    // Pass both the pricing actually shown AND the dish breakdown that
    // backs it, so the cart entry carries everything needed to both
    // display and total this combo accurately.
    onAdd(combo, selections, pricing, dishesBreakdown);
    onClose();
  };

  return (
    <>
      <div className="mi-overlay" onClick={onClose} />
      <div className="mi-combo-modal">
        <div className="mi-cart-drag-handle" />

        <div className="mi-combo-modal-hd">
          <button className="mi-cart-close mi-combo-modal-x" onClick={onClose}>✕</button>
          {combo.tag && <span className="mi-combo-modal-eyebrow">{combo.tag}</span>}
          <h2 className="mi-combo-modal-title">{combo.name}</h2>
          {combo.description && <p className="mi-combo-modal-sub">{combo.description}</p>}
        </div>

        <div className="mi-combo-modal-body">
          {isChoice ? (
            groups.map((g, gi) => (
              <div className="mi-combo-group" key={gi}>
                <div className="mi-combo-group-hd">
                  <span className="mi-combo-group-name">{g.groupName}</span>
                  <span className="mi-combo-group-choose">
                    Choose {g.minSelect === g.maxSelect ? g.minSelect : `${g.minSelect}-${g.maxSelect}`}
                  </span>
                </div>
                <div className="mi-combo-options">
                  {(g.options || []).map((opt, oi) => {
                    const dish = opt.dishId || {};
                    const isSelected = selected[gi]?.has(oi);
                    const unavailable = dish.isAvailable === false || dish.isRemoved;
                    return (
                      <button
                        key={oi}
                        type="button"
                        className={`mi-combo-option ${isSelected ? 'mi-combo-option-on' : ''} ${unavailable ? 'mi-combo-option-off' : ''}`}
                        onClick={() => !unavailable && toggleOption(gi, oi, g.maxSelect || 1)}
                        disabled={unavailable}
                      >
                        {isSelected
                          ? <CheckCircle2 size={17} className="mi-combo-opt-icon mi-combo-opt-icon-on" />
                          : <Circle size={17} className="mi-combo-opt-icon" />
                        }
                        <span className="mi-combo-opt-name">
                          {dish.name}{unavailable ? ' (Unavailable)' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="mi-combo-group">
              <div className="mi-combo-group-hd">
                <span className="mi-combo-group-name">Includes</span>
              </div>
              <div className="mi-combo-options mi-combo-options-static">
                {(combo.dishes || []).map((d, di) => (
                  <div key={di} className="mi-combo-static-item">
                    <Check size={14} className="mi-combo-static-check" />
                    <span>
                      {d.quantity > 1 ? `${d.quantity}x ` : ''}
                      {d.dishId?.name}{d.size ? ` (${d.size.charAt(0).toUpperCase()}${d.size.slice(1)})` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mi-combo-modal-ft">
          <div className="mi-combo-modal-totals">
            {pricing.regularPrice > pricing.finalPrice && (
              <div className="mi-trow">
                <span>Regular price</span>
                <span className="mi-combo-strike">₹{pricing.regularPrice}</span>
              </div>
            )}
            {pricing.savings > 0 && (
              <div className="mi-trow mi-saved"><span>You save</span><span>₹{pricing.savings}</span></div>
            )}
            <div className="mi-trow mi-grand"><span>Combo total</span><span>₹{pricing.finalPrice}</span></div>
          </div>
          <button className="mi-place-btn" onClick={handleAdd} disabled={!isValid}>
            Add Combo · ₹{pricing.finalPrice}
          </button>
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   MenuItems
═══════════════════════════════════════════════════════════ */
const MenuItems = ({ socket }) => {
  const cafeid = window.location.pathname.split('/')[2];

  /* ── state ─────────────────────────────────────────────── */
  const [cafeInfo, setCafeInfo]             = useState(null);
  const [menu, setMenu]                     = useState([]);
  const [cart, setCart]                     = useState({});
  const [cartSize, setCartSize]             = useState(0);
  const [showCart, setShowCart]             = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const sectionRefs = useRef({});
  const [orderType, setOrderType]           = useState('dineIn');
  const [tables, setTables]                 = useState([]);
  const [selectedTable, setSelectedTable]   = useState(null);
  const [loadingTables, setLoadingTables]   = useState(false);
  const [instruction, setInstruction]       = useState('');
  const [placing, setPlacing]               = useState(false);
  const [orderError, setOrderError]         = useState('');
  const [searchQuery, setSearchQuery]       = useState('');
  const [isSearchOpen, setIsSearchOpen]     = useState(false);
  const searchInputRef = useRef(null);
  const [dietFilter, setDietFilter]         = useState('all'); // 'all'|'veg'|'nonveg'
  /* ── new: sort + price/discount filters ─────────────────── */
  const [sortBy, setSortBy]                 = useState('default'); // 'default'|'priceLow'|'priceHigh'
  const [priceRange, setPriceRange]         = useState('all');     // see PRICE_RANGES
  const [discountOnly, setDiscountOnly]     = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [isLogin, setIsLogin]               = useState(false);
  const [checkingLogin, setCheckingLogin]   = useState(true);
  const [showLoginGate, setShowLoginGate]   = useState(false);
  const [loginError, setLoginError]         = useState('');
  const [lastOrder, setLastOrder]           = useState(null);
  const [showRepeatBanner, setShowRepeatBanner] = useState(false);
  const [repeatAdded, setRepeatAdded]       = useState(false);
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);

  /* ── combos ───────────────────────────────────────────────
     `combos` = catalogue fetched from /combo/public/:cafeid.
     `comboCart` is a SEPARATE cart from `cart` (dish cart), keyed by
     `combo-{comboId}-{selectionSignature}` so the same CHOICE combo
     can be added twice with different picks and stay as distinct
     lines. `activeComboModal` holds whichever combo's modal is open. */
  const [combos, setCombos]                 = useState([]);
  const [comboCart, setComboCart]           = useState({});
  const [activeComboModal, setActiveComboModal] = useState(null);
  const [nowTick, setNowTick]               = useState(Date.now());

  /* ── "item added to cart" toast ─────────────────────────── */
  const [toastMessage, setToastMessage] = useState(null);
  const toastTimeoutRef = useRef(null);

  const showToast = useCallback((message) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(message);
    toastTimeoutRef.current = setTimeout(() => setToastMessage(null), TOAST_DURATION_MS);
  }, []);

  useEffect(() => () => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
  }, []);

  /* ── data fetch ─────────────────────────────────────────── */
  const checkLogin = async () => {
    const token = localStorage.getItem('token');
    if (!token) { setIsLogin(false); setCheckingLogin(false); return; }
    try {
      const res = await fetch(`${host}/user/vlogin`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      setIsLogin(res.ok);
    } catch { setIsLogin(false); }
    setCheckingLogin(false);
  };

  const fetchLastOrder = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${host}/order/lastorder`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.items?.length) {
          setLastOrder(data);
          if (data.restaurantId == cafeid) {
            setShowRepeatBanner(true);
          }
        }
      }
    } catch (err) { console.error('lastorder:', err); }
  };

  const fetchMenu = async () => {
    try {
      const res = await fetch(`${host}/order/fetchMenu?cafeid=${cafeid}&src=qr`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        const { menu: menuData = [], ...info } = data;
        setCafeInfo(info);
        setMenu(menuData);
        if (menuData.length > 0 && menuData[0].categories?.length > 0) {
          setActiveCategory(`${menuData[0].type}-${menuData[0].categories[0].name}`);
        }
      }
    } catch (err) { console.error('fetchMenu:', err); }
  };

  const fetchCombos = async () => {
    try {
      const res = await fetch(`${host}/combo/public/${cafeid}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setCombos(data.data || []);
      }
    } catch (err) { console.error('fetchCombos:', err); }
  };

  /* Fetches available tables. `silent` skips the loading spinner so
     background polling refreshes don't flicker the UI. Also reconciles
     the currently selected table against the fresh list — if it got
     booked by someone else in the meantime, the selection is cleared. */
  const fetchTables = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingTables(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${host}/tables/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ restaurantId: cafeid }),
      });
      const data = await r.json();
      if (data.success) {
        const available = data.data.filter(t => t.status === 'available' && t.isActive);
        setTables(available);
        setSelectedTable(prev => {
          if (!prev) return prev;
          return available.find(t => t._id === prev._id) || null;
        });
      }
    } catch (err) { console.error('fetchTables:', err); }
    finally { if (!silent) setLoadingTables(false); }
  }, [cafeid]);

  useEffect(() => { checkLogin(); fetchLastOrder(); fetchMenu(); fetchCombos(); }, []);

  // Reset table selection whenever the guest isn't ordering dine-in.
  useEffect(() => {
    if (orderType !== 'dineIn') {
      setTables([]);
      setSelectedTable(null);
    }
  }, [orderType]);

  // Only hit the tables API while the order/cart modal is actually open
  // and the guest is on dine-in — avoids polling the server in the
  // background for carts that are never opened. Refreshes every 3s so
  // table availability stays current while the guest is picking one.
  useEffect(() => {
    if (orderType !== 'dineIn' || !showCart) return;

    fetchTables();
    const interval = setInterval(() => fetchTables({ silent: true }), TABLE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [orderType, showCart, fetchTables]);

  useEffect(() => {
    socket.emit('join-room', cafeid);
    return () => socket.disconnect();
  }, []);
  useEffect(() => {
    if (isSearchOpen) setTimeout(() => searchInputRef.current?.focus(), 80);
  }, [isSearchOpen]);

  // Show the "repeat last order" banner only once both the menu and the
  // last order are loaded, and at least one item is matchable in the menu.
  useEffect(() => {
    if (!lastOrder?.items?.length || !menu.length) return;

    const lookup = {};
    menu.forEach(m => m.categories?.forEach(c =>
      c.dishes?.forEach(d => { if (d._id) lookup[d._id] = true; })
    ));

    const hasMatch = lastOrder.items.some(i => lookup[i.dishId]) && lastOrder.restaurantId == cafeid;
    if (hasMatch) setShowRepeatBanner(true);
  }, [lastOrder, menu]);

  /* ── combo countdown ticker — only runs while at least one combo has
     a validTill within the next 48h, so we're not re-rendering every
     second for the common case of no time-limited combos. ─────────── */
  const hasCountdownCombo = useMemo(() => combos.some(c => {
    if (!c.validTill) return false;
    const diff = new Date(c.validTill).getTime() - Date.now();
    return diff > 0 && diff < COMBO_COUNTDOWN_WINDOW_MS;
  }), [combos]);

  useEffect(() => {
    if (!hasCountdownCombo) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasCountdownCombo]);

  /* ── dish lookup by _id ─────────────────────────────────── */
  const dishLookup = useMemo(() => {
    const map = {};
    menu.forEach(m => m.categories?.forEach(c =>
      c.dishes?.forEach(d => { if (d._id) map[d._id] = { dish: d, mealType: m.type, categoryName: c.name }; })
    ));
    return map;
  }, [menu]);

  /* ── most ordered ────────────────────────────────────────── */
  const mostOrderedDishes = useMemo(() => {
    const all = [];
    menu.forEach(m => m.categories?.forEach(c =>
      c.dishes?.forEach(d => {
        if (d._id && d.isAvailable !== false)
          all.push({ dish: d, mealType: m.type, categoryName: c.name, count: getDummyCount(d._id) });
      })
    ));
    return all.sort((a, b) => b.count - a.count).slice(0, MOST_ORDERED_TOP);
  }, [menu]);

  /* ── floating leaf particles (veg filter effect) ─────────
     Stable, precomputed configs so leaves don't re-randomize
     or jump around on every re-render — only mounted while
     dietFilter === 'veg'. */
  const LEAF_COUNT = 9;
  const vegLeafConfigs = useMemo(() => (
    Array.from({ length: LEAF_COUNT }).map((_, i) => ({
      id: i,
      left: (i * 37 + 6) % 94,                       // spread across width, deterministic
      size: 14 + ((i * 5) % 3) * 5,                   // 14 / 19 / 24 px
      duration: 8 + (i % 4) * 2.2,                    // 8–14.6s per fall
      delay: (i * 0.85) % 6,                          // staggered starts
      drift: (i % 2 === 0 ? 1 : -1) * (24 + (i % 3) * 14), // sideways sway
      spin: 200 + (i % 3) * 90,
    }))
  ), []);

  /* ── are any non-search filters active? ─────────────────── */
  const hasActiveFilters = dietFilter !== 'all' || priceRange !== 'all' || discountOnly || sortBy !== 'default';
  const activeFilterCount =
    (dietFilter !== 'all' ? 1 : 0) +
    (priceRange !== 'all' ? 1 : 0) +
    (discountOnly ? 1 : 0) +
    (sortBy !== 'default' ? 1 : 0);

  const clearAllFilters = () => {
    setDietFilter('all');
    setPriceRange('all');
    setDiscountOnly(false);
    setSortBy('default');
  };

  /* ── filtered + sorted + searched menu ───────────────────── */
  const filteredMenu = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    const applyDiet = (dishes) =>
      dietFilter === 'all' ? dishes : dishes.filter(d => d.type === dietFilter);

    const applyPriceAndDiscount = (dishes) =>
      dishes.filter(d => {
        if (discountOnly && !dishHasDiscount(d)) return false;
        if (priceRange !== 'all') {
          const p = getDisplayPrice(d);
          if (priceRange === 'under200' && !(p < 200)) return false;
          if (priceRange === '200to400' && !(p >= 200 && p <= 400)) return false;
          if (priceRange === 'above400' && !(p > 400)) return false;
        }
        return true;
      });

    const applySort = (dishes) => {
      if (sortBy === 'priceLow')  return [...dishes].sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
      if (sortBy === 'priceHigh') return [...dishes].sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
      return dishes;
    };

    const pipeline = (dishes) => applySort(applyPriceAndDiscount(applyDiet(dishes || [])));

    if (!q) {
      return menu
        .map(m => ({
          ...m,
          categories: m.categories
            ?.map(c => ({ ...c, dishes: pipeline(c.dishes) }))
            .filter(c => c.dishes.length > 0),
        }))
        .filter(m => m.categories?.length > 0);
    }

    const results = [];
    menu.forEach(m =>
      m.categories?.forEach(c => {
        const matches = (c.dishes || []).filter(d => d.name.toLowerCase().includes(q));
        pipeline(matches).forEach(d => results.push({ dish: d, mealType: m.type, categoryName: c.name }));
      })
    );
    if (!results.length) return [];
    return [{
      type: 'Search Results',
      categories: [{
        name: `${results.length} item${results.length !== 1 ? 's' : ''} found`,
        dishes: results.map(r => ({ ...r.dish, _mealType: r.mealType, _categoryName: r.categoryName })),
      }],
    }];
  }, [searchQuery, menu, dietFilter, sortBy, priceRange, discountOnly]);

  const isSearching = searchQuery.trim().length > 0;
  const showMostOrdered = !isSearching && !hasActiveFilters && mostOrderedDishes.length > 0;
  const showCombos = !isSearching && combos.length > 0;
  const showEmptyResults = (isSearching || hasActiveFilters) && menu.length > 0 && filteredMenu.length === 0;

  /* ── table grouping (area-wise) ──────────────────────────── */
  const groupedTables = useMemo(() => {
    const groups = {};
    tables.forEach(t => {
      const areaName = t.areaId?.name || 'General';
      if (!groups[areaName]) groups[areaName] = [];
      groups[areaName].push(t);
    });
    Object.values(groups).forEach(list => {
      list.sort((a, b) => {
        const an = parseInt(a.tableNumber, 10);
        const bn = parseInt(b.tableNumber, 10);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
        return String(a.tableNumber).localeCompare(String(b.tableNumber));
      });
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [tables]);

  /* ── cart helpers ─────────────────────────────────────────── */
  const calcPrice = (quantity, price, discount) =>
    Math.ceil(quantity * (price - price * discount / 100) * 100) / 100;

  /* `silent` lets callers (like "repeat last order", which adds several
     dishes in a loop) suppress the per-item toast and show one combined
     confirmation instead. */
  const handleAddToCart = useCallback((dish, size, mealType, categoryName, { silent = false } = {}) => {
    const key = `${dish.name}-${size}`;
    const sizeData = dish[size];
    if (!sizeData?.price) return;
    setCart(prev => {
      const u = { ...prev };
      if (u[key]) {
        if (u[key].quantity < 10) u[key] = { ...u[key], quantity: u[key].quantity + 1 };
      } else {
        u[key] = {
          name: dish.name, quantity: 1,
          price: sizeData.price, discount: sizeData.discount || 0,
          pack: size, image: dish.image, type: dish.type,
          isTaxable: dish.isTaxable !== false,
          mealType, category: categoryName, dishId: dish._id,
        };
        setCartSize(p => p + 1);
      }
      return u;
    });
    if (!silent) showToast(`${dish.name} added to cart`);
  }, [showToast]);

  const handleInc = useCallback((key) => {
    setCart(prev => {
      const u = { ...prev };
      if (u[key] && u[key].quantity < 10) u[key] = { ...u[key], quantity: u[key].quantity + 1 };
      return u;
    });
  }, []);

  const handleDec = useCallback((key) => {
    setCart(prev => {
      const u = { ...prev };
      if (!u[key]) return u;
      if (u[key].quantity > 1) {
        u[key] = { ...u[key], quantity: u[key].quantity - 1 };
      } else {
        delete u[key];
        setCartSize(p => p - 1);
      }
      return u;
    });
  }, []);

  /* ── combo cart helpers ───────────────────────────────────
     Keyed by comboId + a signature of the chosen options, so adding
     the same CHOICE combo twice with different picks creates two
     distinct lines, while identical repeat-adds just bump quantity.

     `pricingOverride` is the pricing object ComboModal actually showed
     (finalPrice is always the owner-set price). `dishesBreakdown` is
     the full per-dish record backing that combo — {dishId, name, size,
     quantity, price, discount} for every dish it actually contains,
     for BOTH combo types. This is what lets the cart display combo
     contents and compute accurate savings, instead of relying on a
     single opaque number. */
  const addComboToCart = useCallback((combo, selections = [], pricingOverride = null, dishesBreakdown = []) => {
    const selSig = selections.map(s => `${s.dishId}-${s.size}`).sort().join('|');
    const key = `combo-${combo._id}-${selSig}`;
    const pricing = pricingOverride || getComboPricing(combo);

    setComboCart(prev => {
      const u = { ...prev };
      if (u[key]) {
        if (u[key].quantity < 10) u[key] = { ...u[key], quantity: u[key].quantity + 1 };
      } else {
        u[key] = {
          comboId: combo._id,
          name: combo.name,
          image: combo.image,
          comboStructure: combo.comboStructure,
          quantity: 1,
          selections,
          dishesBreakdown, // ← every dish this combo contains, with real price/discount/qty
          unitPrice: pricing.finalPrice, // owner-configured — what's actually charged
          isTaxable: combo.isTaxable !== false,
        };
        setCartSize(p => p + 1);
      }
      return u;
    });
    showToast(`${combo.name} added to cart`);
  }, [showToast]);

  const handleComboInc = useCallback((key) => {
    setComboCart(prev => {
      const u = { ...prev };
      if (u[key] && u[key].quantity < 10) u[key] = { ...u[key], quantity: u[key].quantity + 1 };
      return u;
    });
  }, []);

  const handleComboDec = useCallback((key) => {
    setComboCart(prev => {
      const u = { ...prev };
      if (!u[key]) return u;
      if (u[key].quantity > 1) {
        u[key] = { ...u[key], quantity: u[key].quantity - 1 };
      } else {
        delete u[key];
        setCartSize(p => p - 1);
      }
      return u;
    });
  }, []);

  /* ── repeat last order:
       match lastOrder.items[].dishId → dishLookup
       find best-matching size (by price), fallback to first valid size
  ─── */
  const handleRepeatLastOrder = useCallback(() => {
    if (!lastOrder?.items?.length) return;
    let added = 0;
    lastOrder.items.forEach(orderItem => {
      const found = dishLookup[orderItem.dishId];
      if (!found) return;
      const { dish, mealType, categoryName } = found;
      if (dish.isAvailable === false) return;
      const sizes = ['small', 'medium', 'large'].filter(sz => dish[sz]?.price > 0);
      if (!sizes.length) return;
      // Try to match original size by name first, then fall back to closest price, then first
      const size =
        sizes.find(sz => sz === orderItem.pack) ||
        sizes.reduce((best, sz) => {
          const diff = Math.abs(dish[sz].price - (orderItem.price || 0));
          const bestDiff = Math.abs(dish[best].price - (orderItem.price || 0));
          return diff < bestDiff ? sz : best;
        }, sizes[0]);
      for (let i = 0; i < (orderItem.quantity || 1); i++) {
        handleAddToCart(dish, size, mealType, categoryName, { silent: true });
      }
      added++;
    });
    if (added > 0) {
      setRepeatAdded(true);
      setShowRepeatBanner(false);
      showToast(`${added} item${added !== 1 ? 's' : ''} added from your last order`);
      setTimeout(() => setShowCart(true), 320);
    }
  }, [lastOrder, dishLookup, handleAddToCart, showToast]);

  /* ── totals ───────────────────────────────────────────────── */
  const calcTotals = () => {
    let taxSub = 0, nonTaxSub = 0;
    let fullPriceTotal = 0; // sum of every dish's listed price — standalone AND inside combos

    for (const k in cart) {
      const { quantity, price, discount, isTaxable } = cart[k];
      const net = quantity * price * (1 - (discount || 0) / 100);
      if (isTaxable === false) nonTaxSub += net;
      else                     taxSub    += net;

      // Undiscounted price for this dish line.
      fullPriceTotal += quantity * price;
    }

    for (const k in comboCart) {
      const { quantity, unitPrice, dishesBreakdown, isTaxable } = comboCart[k];
      const net = quantity * unitPrice; // what's actually charged for this combo
      if (isTaxable === false) nonTaxSub += net;
      else                     taxSub    += net;

      // Sum of each dish's own listed price × its quantity inside one
      // combo, times how many of that combo were ordered — the actual
      // "total price of each dish" figure, backed by a real list of
      // dishes (set when the combo was added to cart) rather than a
      // single opaque stored number.
      const comboDishesTotal = (dishesBreakdown || []).reduce((s, d) => s + d.price * d.quantity, 0);
      fullPriceTotal += quantity * comboDishesTotal;
    }

    taxSub    = Math.round(taxSub    * 100) / 100;
    nonTaxSub = Math.round(nonTaxSub * 100) / 100;
    const sub = Math.round((taxSub + nonTaxSub) * 100) / 100; // what's actually charged, tax excluded

    // Dynamic, per-line tax breakdown driven by the TAXES config.
    // Each line is rounded independently (matches how the amounts are
    // meant to be stored/reported per the backend Tax schema), and the
    // grand total tax is the sum of those rounded lines.
    const taxBreakdown   = computeTaxBreakdown(taxSub);
    const totalTaxAmount = Math.round(taxBreakdown.reduce((s, t) => s + t.amount, 0) * 100) / 100;

    const total = Math.ceil(sub + totalTaxAmount);

    // Direct subtraction: sum of every dish's own price (standalone +
    // inside every combo) minus what's actually being charged before
    // tax. No reverse tax math, no multi-stage rounding chain.
    const saved = Math.max(0, Math.round((fullPriceTotal - sub) * 100) / 100);

    return {
      taxSub, nonTaxSub, sub, total, saved,
      taxBreakdown, totalTaxAmount,
      hasMixed: taxSub > 0 && nonTaxSub > 0,
      hasNonTaxable: nonTaxSub > 0,
    };
  };

  /* ── place order ──────────────────────────────────────────── */
  const placeOrder = async () => {
    setOrderError('');
    const hasDishItems  = Object.keys(cart).length > 0;
    const hasComboItems = Object.keys(comboCart).length > 0;
    if (!hasDishItems && !hasComboItems) { setOrderError('Your cart is empty.'); return; }
    if (orderType === 'dineIn' && !selectedTable) {
      setOrderError('Please select a table to continue.'); return;
    }
    setPlacing(true);
    setShowLoginGate(false);
    try {
      const token = localStorage.getItem('token');

      // Only send what the guest WANTS — dishId, size, quantity for
      // standalone dishes, and comboId + quantity + their group
      // selections for combos. Price, discount, name, and tax are all
      // recomputed server-side from the live menu/combo config, so a
      // tampered request body can't be used to fake a cheaper order,
      // invent items that don't exist, or swap a combo's dish for a
      // pricier one.
      const items = Object.values(cart).map(item => ({
        dishId: item.dishId,
        size: item.pack,
        quantity: item.quantity,
      }));

      const comboItems = Object.values(comboCart).map(c => ({
        comboId: c.comboId,
        quantity: c.quantity,
        selections: c.selections.map(s => ({
          groupName: s.groupName,
          dishId: s.dishId,
          size: s.size,
        })),
      }));

      const payload = {
        items, combos: comboItems, cafeid, instruction,
        orderType: orderType === 'dineIn' ? 'dine-in' : 'takeaway',
      };
      if (orderType === 'dineIn' && selectedTable) {
        payload.tableId     = selectedTable._id;
        payload.tableNumber = selectedTable.tableNumber; // harmless if backend ignores it
        payload.areaId      = selectedTable.areaId?._id || null;
        payload.areaName    = selectedTable.areaId?.name || 'General'; // harmless if backend ignores it
      }

      const res = await fetch(`${host}/order/placeorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        socket.emit('message', { message: { ...data }, room: cafeid });
        window.location.href = `/orderstatus/${data._id}`;
      } else {
        setOrderError(data?.msg || 'Table is booked by someone else, try using another table.');
        if (res.status === 409 && data?.msg?.toLowerCase().includes('table')) {
          fetchTables(); setSelectedTable(null);
        }
      }
    } catch { setOrderError('Network error. Please check your connection and try again.'); }
    finally { setPlacing(false); }
  };

  const handlePlaceOrderClick = () => {
    if (isLogin) placeOrder();
    else { setShowLoginGate(true); setLoginError(''); }
  };

 const onGoogleSuccess = async (credentialResponse) => {
  setLoginError('');
  try {
    const resp = await axios.post(`${host}/user/login`, {
      credential: credentialResponse.credential, // signed ID token, verified server-side
    });
    if (resp.status === 200) {
      localStorage.setItem('token', resp.headers.authorization);
      localStorage.setItem('providerlogintime', String(Date.now()));
      setIsLogin(true);
      await placeOrder();
    } else {
      setLoginError('Login failed. Please try again.');
    }
  } catch {
    setLoginError('Login failed. Please try again.');
  }
};

  const scrollToCategory = (id) => {
    setActiveCategory(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ── open/closed status ───────────────────────────────────── */
  const cafeStatus = useMemo(() => {
    if (!cafeInfo?.opensAt && !cafeInfo?.closeAt) return null;
    const now   = new Date();
    const hr    = now.getHours() + now.getMinutes() / 60;
    const open  = cafeInfo.opensAt || 0;
    const close = cafeInfo.closeAt || 24;
    const fmtHr = (h) => {
      const hh = Math.floor(h), mm = Math.round((h - hh) * 60);
      return `${hh % 12 || 12}${mm ? `:${String(mm).padStart(2,'0')}` : ''} ${hh >= 12 ? 'PM' : 'AM'}`;
    };
    return { isOpen: hr >= open && hr < close, closeLabel: fmtHr(close), openLabel: fmtHr(open) };
  }, [cafeInfo]);

  const totals        = calcTotals();
  const cartItems      = Object.entries(cart);
  const comboCartItems = Object.entries(comboCart);
  const hasCart         = cartItems.length > 0 || comboCartItems.length > 0;

  /* ═══════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════ */
  return (
    <>
      <SEOMeta
        title={cafeInfo?.name || null}
        description="Find the best restaurants and cafés near you."
        url="https://www.restropilots.com"
      />

      <div className={`mi-shell${dietFilter === 'veg' ? ' mi-veg-active' : ''}`}>

        {/* ── Floating leaves — veg filter effect ── */}
        {dietFilter === 'veg' && (
          <div className="mi-leaf-field" aria-hidden="true">
            {vegLeafConfigs.map(leaf => (
              <Leaf
                key={leaf.id}
                size={leaf.size}
                className="mi-leaf-particle"
                style={{
                  left: `${leaf.left}%`,
                  animationDuration: `${leaf.duration}s`,
                  animationDelay: `${leaf.delay}s`,
                  '--mi-leaf-drift': `${leaf.drift}px`,
                  '--mi-leaf-spin': `${leaf.spin}deg`,
                }}
              />
            ))}
          </div>
        )}

        {/* ── "item added to cart" toast ── */}
        {toastMessage && (
          <div className="mi-toast-layer" aria-live="polite">
            <div key={toastMessage} className="mi-toast">
              <span className="mi-toast-icon"><Check size={12} color="#fff" /></span>
              <span className="mi-toast-text">{toastMessage}</span>
            </div>
          </div>
        )}

        {/* ── Cafe header ── */}
       {cafeInfo?.name && (
  <div className="mi-cafe-header">
    <h1 className="mi-cafe-name">{cafeInfo.name}</h1>
    <div className="mi-cafe-tags">
      <span className="mi-cafe-badge mi-badge-solid">
        <Link to={`/moredetails?cafeid=${cafeid}`} className="mi-badge-link">
          <Store size={11} /> Visit Store
        </Link>
      </span>
      {cafeInfo.menuType === 'veg' && (
        <span className="mi-cafe-badge mi-badge-veg"><Leaf size={10} />Pure Veg</span>
      )}
      <span className="mi-cafe-subtitle">
        {[cafeInfo.cuisine || 'Multi-cuisine', 'Dine-in & Takeaway']
          .filter(Boolean)
          .join(' · ')}
      </span>
    </div>
  </div>
)}

        {/* ── Repeat last order banner ── */}
        {showRepeatBanner && lastOrder && !repeatAdded && (
          <div className="mi-repeat-banner">
            <div className="mi-repeat-left">
              <div className="mi-repeat-icon-wrap"><RotateCcw size={14} /></div>
              <div className="mi-repeat-text">
                <span className="mi-repeat-label">Repeat Last Order</span>
                <span className="mi-repeat-items">
                  {lastOrder.items.slice(0, 2).map(i => i.item).join(', ')}
                  {lastOrder.items.length > 2 && ` +${lastOrder.items.length - 2} more`}
                </span>
              </div>
            </div>
            <div className="mi-repeat-actions">
              <button className="mi-repeat-btn" onClick={handleRepeatLastOrder}>Repeat</button>
              <button className="mi-repeat-dismiss" onClick={() => setShowRepeatBanner(false)}>
                <X size={13} />
              </button>
            </div>
          </div>
        )}

        {/* ── Sticky category + filter bar ── */}
       <div className="mi-cat-bar">
  {/* Row 1: search + diet filters + advanced filter toggle */}
  <div className="mi-filter-row">
    <div className="mi-search-inline">
      <Search size={14} className="mi-search-icon" />
      <input
        className="mi-search-input"
        type="text"
        placeholder="Search dishes"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
      />
      {searchQuery && (
        <button
          className="mi-search-clear"
          onClick={() => setSearchQuery('')}
          aria-label="Clear search"
        >
          <X size={12} />
        </button>
      )}
    </div>

    <div className="mi-diet-group">
      <button
        className={`mi-diet-chip ${dietFilter === 'all' ? 'mi-diet-all-on' : ''}`}
        onClick={() => setDietFilter('all')}
      >All</button>
      <button
        className={`mi-diet-chip mi-diet-veg-chip ${dietFilter === 'veg' ? 'mi-diet-veg-on' : ''}`}
        onClick={() => setDietFilter(dietFilter === 'veg' ? 'all' : 'veg')}
      >
        <VegNonVegIcon type="veg" size={11} />Veg
      </button>
      <button
        className={`mi-diet-chip mi-diet-nveg-chip ${dietFilter === 'nonveg' ? 'mi-diet-nveg-on' : ''}`}
        onClick={() => setDietFilter(dietFilter === 'nonveg' ? 'all' : 'nonveg')}
      >
        <VegNonVegIcon type="nonveg" size={11} />Non-Veg
      </button>
    </div>

    <button
      className={`mi-filter-toggle-btn ${showFilterPanel ? 'mi-filter-toggle-on' : ''} ${activeFilterCount - (dietFilter !== 'all' ? 1 : 0) > 0 ? 'mi-filter-toggle-active' : ''}`}
      onClick={() => setShowFilterPanel(v => !v)}
      aria-expanded={showFilterPanel}
    >
      <SlidersHorizontal size={13} />
      {(priceRange !== 'all' || discountOnly || sortBy !== 'default') && (
        <span className="mi-filter-count-dot" />
      )}
    </button>
  </div>

  {/* Row 1b: expandable sort / price / discount panel */}
  {showFilterPanel && (
    <div className="mi-adv-filter-row">
      <div className="mi-adv-filter-scroll">
        <select
          className="mi-sort-select"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          aria-label="Sort by"
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>

        {PRICE_RANGES.map(r => (
          <button
            key={r.key}
            className={`mi-price-chip ${priceRange === r.key ? 'mi-price-chip-on' : ''}`}
            onClick={() => setPriceRange(priceRange === r.key ? 'all' : r.key)}
          >
            {r.label}
          </button>
        ))}

        <button
          className={`mi-price-chip mi-discount-chip ${discountOnly ? 'mi-price-chip-on' : ''}`}
          onClick={() => setDiscountOnly(v => !v)}
        >
          <Tag size={10} />On Discount
        </button>

        {hasActiveFilters && (
          <button className="mi-clear-filters-btn" onClick={clearAllFilters}>
            <X size={11} />Clear all
          </button>
        )}
      </div>
    </div>
  )}

  {/* Row 2: category tabs */}
  <div className="mi-cat-scroll">
    <button
      className={`mi-cat-tab ${activeCategory === null ? 'mi-cat-on' : ''}`}
      onClick={() => setActiveCategory(null)}
    >
      All
    </button>
    {menu.map((m, i) =>
      m.categories?.map((c, j) => {
        const id = `${m.type}-${c.name}`;
        return (
          <button
            key={`${i}-${j}`}
            className={`mi-cat-tab ${activeCategory === id ? 'mi-cat-on' : ''}`}
            onClick={() => scrollToCategory(id)}
          >
            {c.name}
          </button>
        );
      })
    )}
  </div>
</div>

        {/* ── Menu body ── */}
        <div className="mi-menu-body">

          {/* Empty search / empty filtered results */}
          {showEmptyResults && (
            <div className="mi-search-empty">
              <Search size={36} color="var(--faint)" />
              {isSearching ? (
                <p>No dishes found for "<strong>{searchQuery}</strong>"</p>
              ) : (
                <p>No dishes match your filters</p>
              )}
              <span>Try a different {isSearching ? 'keyword' : 'filter combination'}</span>
              {hasActiveFilters && (
                <button className="mi-clear-filters-btn mi-clear-filters-btn-lg" onClick={clearAllFilters}>
                  <X size={12} />Clear filters
                </button>
              )}
            </div>
          )}

          {/* Empty menu */}
          {!isSearching && menu.length === 0 && (
            <div className="mi-empty-menu">
              <div className="mi-em-scene">
                <div className="mi-em-hat">
                  <div className="mi-em-hat-top" /><div className="mi-em-hat-stripe" /><div className="mi-em-hat-brim" />
                </div>
                <div className="mi-em-steam">
                  <div className="mi-em-s1" /><div className="mi-em-s2" /><div className="mi-em-s3" />
                </div>
                <div className="mi-em-dome" /><div className="mi-em-dome-handle" />
                <div className="mi-em-plate"><div className="mi-em-plate-shine" /></div>
              </div>
              <h2 className="mi-em-title">The kitchen is resting</h2>
              <p className="mi-em-sub">Our chef is busy crafting the menu. Come back soon — something delicious is on its way.</p>
              <div className="mi-em-tags">
                <span className="mi-em-tag">Fresh ingredients</span>
                <span className="mi-em-tag">Made with love</span>
                <span className="mi-em-tag">Coming soon</span>
              </div>
              <div className="mi-em-dots">
                <div className="mi-em-dot" /><div className="mi-em-dot" /><div className="mi-em-dot" />
              </div>
            </div>
          )}

          {/* ── Combos & Offers section ── */}
          {showCombos && (
            <div className="mi-section mi-combo-section">
              <div className="mi-sec-hd">
                <p className="mi-sec-eyebrow">
                  <Sparkles size={10} style={{ display:'inline-block', verticalAlign:'middle', marginRight:3 }} />
                  Deals For You
                </p>
                <h2>Combos & Offers</h2>
              </div>
              <div className="mi-combo-scroll">
                {combos.map((combo, idx) => (
                  <ComboCard
                    key={combo._id}
                    combo={combo}
                    index={idx}
                    nowTick={nowTick}
                    onOpen={setActiveComboModal}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Most Ordered section ── */}
          {showMostOrdered && (
            <div className="mi-section mi-most-section">
              <div className="mi-sec-hd">
                <p className="mi-sec-eyebrow">
                  <Flame size={10} style={{ display:'inline-block', verticalAlign:'middle', marginRight:3 }} />
                  Today's Picks
                </p>
                <h2>Most Ordered</h2>
              </div>
              <div className="mi-dish-grid">
                {mostOrderedDishes.map((item, idx) => (
                  <DishCard
                    key={item.dish._id || idx}
                    dish={item.dish}
                    mealType={item.mealType}
                    categoryName={item.categoryName}
                    onAddToCart={handleAddToCart}
                    onInc={handleInc}
                    onDec={handleDec}
                    cart={cart}
                    orderedCount={item.count}
                    isTrending={idx < 3}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Regular sections ── */}
          {filteredMenu.map((m, mi) =>
            m.categories?.map((c, ci) => {
              const id = isSearching ? `search-${mi}-${ci}` : `${m.type}-${c.name}`;
              return (
                <div
                  key={`${mi}-${ci}`}
                  ref={el => { if (!isSearching) sectionRefs.current[id] = el; }}
                  className="mi-section"
                >
                  <div className="mi-sec-hd">
                    <p className="mi-sec-eyebrow">{c.name}</p>
                    <h2>{m.type}</h2>
                  </div>
                  <div className="mi-dish-grid">
                    {c.dishes?.map((d, di) => (
                      <DishCard
                        key={di}
                        dish={d}
                        mealType={d._mealType || m.type}
                        categoryName={d._categoryName || c.name}
                        onAddToCart={handleAddToCart}
                        onInc={handleInc}
                        onDec={handleDec}
                        cart={cart}
                        orderedCount={d._id ? getDummyCount(d._id) : null}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Floating cart FAB ── */}
        {cartSize > 0 && !showCart && (
          <div className="mi-cart-fab-wrap">
            <button className="mi-cart-fab" onClick={() => setShowCart(true)}>
              <span className="mi-fab-icon-wrap">
                <ShoppingCart size={20} />
                <span className="mi-fab-count-badge">{cartSize}</span>
              </span>
              <span className="mi-fab-label">View Cart</span>
              <span className="mi-fab-price">₹{totals.total}</span>
            </button>
          </div>
        )}

        {/* ── Combo customize/preview modal ── */}
        {activeComboModal && (
          <ComboModal
            combo={activeComboModal}
            onClose={() => setActiveComboModal(null)}
            onAdd={addComboToCart}
          />
        )}

        {/* ── Cart modal ── */}
        {showCart && (
          <>
            <div className="mi-overlay" onClick={() => setShowCart(false)} />
            <div className="mi-cart-modal">
              <div className="mi-cart-drag-handle" />
              <div className="mi-cart-hd">
                <h2>Your Order</h2>
                <button className="mi-cart-close" onClick={() => setShowCart(false)}>✕</button>
              </div>

              <div className="mi-cart-content">

                {/* Order type toggle */}
                <div className="mi-order-type">
                  {[
                    { key: 'dineIn',   label: 'Dine In',  Icon: Store   },
                    { key: 'takeaway', label: 'Takeaway', Icon: Package },
                  ].map(({ key, label, Icon }) => (
                    <button
                      key={key}
                      className={`mi-ot-btn ${orderType === key ? 'mi-ot-on' : ''}`}
                      onClick={() => setOrderType(key)}
                    >
                      <Icon size={14} /><span>{label}</span>
                    </button>
                  ))}
                </div>

                {/* Table selector */}
                {orderType === 'dineIn' && (
                  <div className="mi-table-wrap">
                    {loadingTables ? (
                      <div className="mi-tbl-load"><div className="mi-tbl-spin" /><span>Loading tables…</span></div>
                    ) : tables.length === 0 ? (
                      <div className="mi-tbl-empty"><p>No tables available</p><span>All tables are currently occupied</span></div>
                    ) : (
                      <>
                        <div className="mi-tbl-lbl-row">
                          <label className="mi-tbl-lbl">Select Your Table</label>
                          <span className="mi-tbl-live" title="Availability refreshes automatically">
                            <span className="mi-tbl-live-dot" />Live
                          </span>
                        </div>
                        {groupedTables.map(([areaName, areaTables]) => (
                          <div key={areaName} className="mi-tbl-area-group">
                            <div className="mi-tbl-area-hd">
                              <span className="mi-tbl-area-name">{areaName}</span>
                              <span className="mi-tbl-area-count">{areaTables.length} available</span>
                            </div>
                            <div className="mi-tbl-grid">
                              {areaTables.map(t => (
                                <button
                                  key={t._id}
                                  className={`mi-tbl-btn ${selectedTable?._id === t._id ? 'mi-tbl-on' : ''}`}
                                  onClick={() => setSelectedTable(t)}
                                >
                                  <MapPin size={11} className="mi-tbl-icon" />
                                  <span className="mi-tbl-num">{t.tableNumber}</span>
                                  <span className="mi-tbl-cap">{t.capacity} seats</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}

                {/* Cart items */}
                <div className="mi-items-wrap">
                  {!hasCart ? (
                    <div className="mi-empty">
                      <ShoppingCart size={36} color="var(--faint)" />
                      <p>Your cart is empty</p>
                    </div>
                  ) : (
                    <>
                      {/* Combo lines — shown above standalone dishes. The
                          dish list under the combo name now comes from
                          dishesBreakdown for BOTH BUNDLE and CHOICE combos
                          (previously only CHOICE combos showed contents). */}
                      {comboCartItems.map(([key, c]) => (
                        <div key={key} className="mi-item mi-combo-cart-item">
                          <div className="mi-combo-cart-icon"><Sparkles size={13} /></div>
                          <div className="mi-item-info">
                            <h4>{c.name}</h4>
                            <p>
                              {c.dishesBreakdown?.length > 0
                                ? c.dishesBreakdown
                                    .map(d => `${d.quantity > 1 ? `${d.quantity}x ` : ''}${d.name}`)
                                    .join(', ')
                                : 'Combo'}
                              {c.isTaxable === false && <span className="mi-mrp-tag"> · MRP</span>}
                            </p>
                          </div>
                          <div className="mi-qty-ctrl">
                            <button onClick={() => handleComboDec(key)}><Minus size={11} style={{color:'whitesmoke'}} /></button>
                            <span>{c.quantity}</span>
                            <button onClick={() => handleComboInc(key)}><Plus size={11} style={{color:'whitesmoke'}} /></button>
                          </div>
                          <div className="mi-item-price">
                            ₹{Math.round(c.quantity * c.unitPrice * 100) / 100}
                          </div>
                        </div>
                      ))}

                      {cartItems.map(([key, item]) => (
                        <div key={key} className="mi-item">
                          <VegNonVegIcon type={item.type} size={12} />
                          <div className="mi-item-info">
                            <h4>{item.name}</h4>
                            <p>
                              {item.pack.charAt(0).toUpperCase() + item.pack.slice(1)} · ₹{item.price}
                              {item.discount > 0 && <span className="mi-disc"> {item.discount}% off</span>}
                              {item.isTaxable === false && <span className="mi-mrp-tag"> · MRP</span>}
                            </p>
                          </div>
                          <div className="mi-qty-ctrl">
                            <button onClick={() => handleDec(key)}><Minus size={11} style={{color:'whitesmoke'}} /></button>
                            <span>{item.quantity}</span>
                            <button onClick={() => handleInc(key)}><Plus size={11} style={{color:'whitesmoke'}} /></button>
                          </div>
                          <div className="mi-item-price">
                            ₹{calcPrice(item.quantity, item.price, item.discount)}
                          </div>
                        </div>
                      ))}

                      {/* Special instructions */}
                      <div className="mi-instructions-wrap">
                        <label className="mi-instr-label">
                          <Clock size={11} />Special Instructions
                        </label>
                        <textarea
                          className="mi-instr-input"
                          placeholder="Less spicy, extra sauce, no onion…"
                          value={instruction}
                          onChange={e => setInstruction(e.target.value)}
                          rows={2}
                          maxLength={200}
                        />
                      </div>

                      {/* Totals */}
                      <div className="mi-totals">
                        {totals.hasMixed ? (<>
                          <div className="mi-trow"><span>Taxable items</span><span>₹{totals.taxSub}</span></div>
                          <div className="mi-trow"><span>MRP items <span className="mi-trow-note">(tax incl.)</span></span><span>₹{totals.nonTaxSub}</span></div>
                          {totals.totalTaxAmount > 0 && (
                            <div className="mi-trow mi-tax-row">
                              <span className="mi-tax-row-label">
                                Total Tax <span className="mi-trow-note">(on taxable)</span>
                                {totals.taxBreakdown.length > 0 && (
                                  <button
                                    type="button"
                                    className="mi-tax-toggle"
                                    onClick={() => setShowTaxBreakdown(v => !v)}
                                    aria-expanded={showTaxBreakdown}
                                  >
                                    <Receipt size={10} />
                                    {showTaxBreakdown ? 'Hide' : 'View'} breakup
                                    {showTaxBreakdown ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                  </button>
                                )}
                              </span>
                              <span>₹{totals.totalTaxAmount}</span>
                            </div>
                          )}
                        </>) : totals.hasNonTaxable ? (<>
                          <div className="mi-trow"><span>Subtotal</span><span>₹{totals.sub}</span></div>
                          <div className="mi-trow mi-trow-info"><span>Prices are MRP inclusive — no extra tax</span></div>
                        </>) : (<>
                          <div className="mi-trow"><span>Subtotal</span><span>₹{totals.sub}</span></div>
                          {totals.totalTaxAmount > 0 && (
                            <div className="mi-trow mi-tax-row">
                              <span className="mi-tax-row-label">
                                Total Tax
                                {totals.taxBreakdown.length > 0 && (
                                  <button
                                    type="button"
                                    className="mi-tax-toggle"
                                    onClick={() => setShowTaxBreakdown(v => !v)}
                                    aria-expanded={showTaxBreakdown}
                                  >
                                    <Receipt size={10} />
                                    {showTaxBreakdown ? 'Hide' : 'View'} breakup
                                    {showTaxBreakdown ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                  </button>
                                )}
                              </span>
                              <span>₹{totals.totalTaxAmount}</span>
                            </div>
                          )}
                        </>)}

                        {showTaxBreakdown && totals.taxBreakdown.length > 0 && (
                          <div className="mi-tax-breakdown">
                            {totals.taxBreakdown.map((t, idx) => (
                              <div key={`${t.name}-${idx}`} className="mi-tax-bd-row">
                                <span className="mi-tax-bd-name">
                                  {t.name}
                                  <span className="mi-tax-bd-pct">({t.percentage}%)</span>
                                </span>
                                <span className="mi-tax-bd-amt">₹{t.amount}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {totals.saved > 0 && (
                          <div className="mi-trow mi-saved"><span>You save 🎉</span><span>₹{totals.saved}</span></div>
                        )}
                        <div className="mi-trow mi-grand"><span>Total</span><span>₹{totals.total}</span></div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Footer */}
              {hasCart && (
                <div className="mi-cart-ft">
                  {orderError && <div className="mi-order-error">⚠️ {orderError}</div>}
                  {!showLoginGate ? (
                    <button
                      className="mi-place-btn"
                      onClick={handlePlaceOrderClick}
                      disabled={placing || checkingLogin}
                    >
                      {placing ? (
                        <><div className="mi-place-spin" />Processing…</>
                      ) : checkingLogin ? (
                        <><div className="mi-place-spin" />Checking…</>
                      ) : `Place Order · ₹${totals.total}`}
                    </button>
                  ) : (
                    <div className="mi-login-gate">
                      <button className="mi-login-gate-back" onClick={() => setShowLoginGate(false)}>← Back</button>
                      <div className="mi-login-gate-body">
                        <div className="mi-login-gate-icon"><LogIn size={26} /></div>
                        <h3 className="mi-login-gate-title">Sign in to place order</h3>
                        <p className="mi-login-gate-sub">
                          Sign-in to track your order, get offers, and earn loyalty points. We use Google sign-in for a fast and secure login experience.
                        </p>
                        {loginError && <div className="mi-login-gate-err">{loginError}</div>}
                        <div className="mi-google-btn-wrap">
  <GoogleLogin
    onSuccess={onGoogleSuccess}
    onError={() => setLoginError('Google sign-in was cancelled or failed.')}
    theme="filled_blue"
    shape="pill"
    size="large"
    text="continue_with"
  />
</div>
                        <p className="mi-login-gate-note">Your ₹{totals.total} order will be placed right after sign-in.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────────────────────
   Per-size pricing helper for a single dish — same discount math
   used everywhere else (getDisplayPrice / calcPrice), just scoped
   to one explicit size instead of a "representative" one.
───────────────────────────────────────────────────────────── */
const SIZE_LABELS = { small: 'Small', medium: 'Medium', large: 'Large' };

const getDishSizePricing = (dish, size) => {
  const sizeData = dish?.[size] || { price: 0, discount: 0 };
  const price    = sizeData.price || 0;
  const discount = sizeData.discount || 0;
  const finalPrice = Math.round((price - price * discount / 100) * 100) / 100;
  return { price, discount, finalPrice };
};

/* ═══════════════════════════════════════════════════════════
   SizePickerSheet — bottom sheet asking which size to add,
   shown only when a dish has more than one available size.
   Every size lists its own MRP, discount %, and final price so
   the guest can compare before picking.
═══════════════════════════════════════════════════════════ */
const SizePickerSheet = ({ dish, sizes, onClose, onConfirm }) => {
  const [chosen, setChosen] = useState(sizes[0]);
  const chosenPricing = getDishSizePricing(dish, chosen);

  // Portaled to document.body — this component is opened from DishCard,
  // which lives inside .mi-dish-grid (a horizontally-scrolling row with
  // -webkit-overflow-scrolling: touch). On mobile, position:fixed
  // descendants of a momentum-scrolling container don't behave like true
  // viewport-fixed elements: they can drift/jump on scroll and swallow
  // touch events instead of passing them to the buttons underneath —
  // which is exactly why size selection looked unresponsive. Rendering
  // via a portal makes this behave the same as ComboModal, which is
  // safe only because it happens to be declared outside that grid.
  return createPortal(
    <>
      <div className="mi-overlay" onClick={onClose} />
      <div className="mi-size-modal">
        <div className="mi-cart-drag-handle" />

        <div className="mi-combo-modal-hd">
          <button className="mi-cart-close mi-combo-modal-x" onClick={onClose}>✕</button>
          <h2 className="mi-combo-modal-title">{dish.name}</h2>
          <p className="mi-combo-modal-sub">Choose a size to add to your cart</p>
        </div>

        <div className="mi-combo-modal-body">
          <div className="mi-combo-group">
            <div className="mi-combo-options">
              {sizes.map(sz => {
                const p = getDishSizePricing(dish, sz);
                const isSelected = chosen === sz;
                return (
                  <button
                    key={sz}
                    type="button"
                    className={`mi-combo-option mi-size-option ${isSelected ? 'mi-combo-option-on' : ''}`}
                    onClick={() => setChosen(sz)}
                  >
                    {isSelected
                      ? <CheckCircle2 size={17} className="mi-combo-opt-icon mi-combo-opt-icon-on" />
                      : <Circle size={17} className="mi-combo-opt-icon" />
                    }
                    <span className="mi-combo-opt-name">{SIZE_LABELS[sz] || sz}</span>
                    <span className="mi-size-option-price">
                      <span className="mi-size-option-final">₹{p.finalPrice}</span>
                      {p.discount > 0 && (
                        <>
                          <span className="mi-size-option-old">₹{p.price}</span>
                          <span className="mi-size-option-disc">{p.discount}% off</span>
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mi-combo-modal-ft">
          <button className="mi-place-btn" onClick={() => onConfirm(chosen)}>
            Add · ₹{chosenPricing.finalPrice}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

/* ═══════════════════════════════════════════════════════════
   DishCard

   Sizes are no longer chosen inline on the card — the card shows
   the starting (lowest) price with its discount breakdown. If a
   dish only has one size, tapping "Add" adds it directly. If it
   has more than one, tapping "Add" opens SizePickerSheet so the
   guest picks a size (each with its own price/discount shown)
   before it's added to the cart.
═══════════════════════════════════════════════════════════ */
const DishCard = ({ dish, mealType, categoryName, onAddToCart, onInc, onDec, cart, orderedCount, isTrending }) => {
  const sizes = ['small', 'medium', 'large'].filter(sz => dish[sz]?.price > 0);
  // A dish with no priced size at all (bad/incomplete menu data) can't
  // actually be added to cart — treat it the same as out-of-stock so the
  // card shows "Out of Stock" instead of a live-looking ₹0 Add button
  // that would silently no-op when tapped.
  const isAvailable = dish.isAvailable !== false && sizes.length > 0;
  const [showSizePicker, setShowSizePicker] = useState(false);

  // Starting price shown on the card — the size with the lowest
  // final (post-discount) price, so the card always advertises the
  // cheapest way to order this dish.
  const startingSize = useMemo(() => {
    if (!sizes.length) return null;
    return sizes.reduce((best, sz) =>
      getDishSizePricing(dish, sz).finalPrice < getDishSizePricing(dish, best).finalPrice ? sz : best
    , sizes[0]);
  }, [dish, sizes]);

  const pricing     = startingSize ? getDishSizePricing(dish, startingSize) : { price: 0, discount: 0, finalPrice: 0 };
  const hasDiscount = pricing.discount > 0;
  const hasImage     = dish.image && !['#','null','undefined','.',''].includes(String(dish.image).trim());
  const isTaxable    = dish.isTaxable !== false;

  const singleSize    = sizes.length === 1 ? sizes[0] : null;
  const singleDishKey = singleSize ? `${dish.name}-${singleSize}` : null;
  const singleCartItem = singleDishKey ? cart[singleDishKey] : null;

  // Total quantity of this dish currently in the cart, across
  // whichever sizes were picked (relevant once there's >1 size,
  // since each size lives under its own cart key).
  const totalQtyInCart = useMemo(
    () => sizes.reduce((sum, sz) => sum + (cart[`${dish.name}-${sz}`]?.quantity || 0), 0),
    [cart, sizes, dish.name]
  );

  const handleAddClick = () => {
    if (!isAvailable) return;
    if (singleSize) {
      onAddToCart(dish, singleSize, mealType, categoryName);
    } else if (sizes.length > 1) {
      setShowSizePicker(true);
    }
  };

  const handleConfirmSize = (size) => {
    onAddToCart(dish, size, mealType, categoryName);
    setShowSizePicker(false);
  };

  return (
    <div className={`mi-dish${!isAvailable ? ' mi-dish-oos' : ''}${isTrending ? ' mi-dish-hot' : ''}`}>
      {!isAvailable && (
        <div className="mi-oos-overlay"><span className="mi-oos-label">Out of Stock</span></div>
      )}
      {isTrending && isAvailable && (
        <div className="mi-hot-badge"><TrendingUp size={8} />Popular</div>
      )}

      {/* Image / placeholder */}
      <div className="mi-dish-img-box">
        {hasImage
          ? <img src={dish.image} alt={dish.name} className="mi-dish-img" loading="lazy" />
          : <GradientPlaceholder name={dish.name} />
        }
        <div className="mi-type-badge">
          <VegNonVegIcon type={dish.type} size={14} />
        </div>
        <div className="mi-dish-name-overlay"><span>{dish.name}</span></div>
        {/* {orderedCount != null && isAvailable && (
          <div className="mi-ordered-pill"><Flame size={8} />{orderedCount} today</div>
        )} */}
      </div>

      {/* Body */}
      <div className="mi-dish-body">
        <div className="mi-price-row">
          <span className="mi-price">₹{pricing.finalPrice}</span>
          {hasDiscount && <>
            <span className="mi-old-price">₹{pricing.price}</span>
            <span className="mi-disc-badge">{pricing.discount}%</span>
          </>}
          {!isTaxable && <span className="mi-mrp-badge">MRP</span>}
        </div>
        {/* {sizes.length > 1 && <span className="mi-starting-note">Starting price · {sizes.length} sizes</span>} */}

        {/* In-card stepper for single-size dishes already in cart */}
        {singleSize && singleCartItem ? (
          <div className="mi-card-stepper">
            <button
              className="mi-step-btn"
              onClick={() => onDec(singleDishKey)}
              aria-label="Remove one"
            ><Minus size={11} /></button>
            <span className="mi-step-count">{singleCartItem.quantity}</span>
            <button
              className="mi-step-btn mi-step-plus"
              onClick={() => isAvailable && onAddToCart(dish, singleSize, mealType, categoryName)}
              disabled={singleCartItem.quantity >= 10}
              aria-label="Add one more"
            ><Plus size={11} /></button>
          </div>
        ) : (
          <button
            className={`mi-add-btn ${totalQtyInCart > 0 ? 'mi-add-in' : ''}`}
            onClick={handleAddClick}
            disabled={!isAvailable}
          >
            <Plus size={12} />
            {!isAvailable ? 'Out of Stock' : totalQtyInCart > 0 ? `Add · ${totalQtyInCart} in cart` : 'Add'}
          </button>
        )}
      </div>

      {showSizePicker && (
        <SizePickerSheet
          dish={dish}
          sizes={sizes}
          onClose={() => setShowSizePicker(false)}
          onConfirm={handleConfirmSize}
        />
      )}
    </div>
  );
};

export default MenuItems;